import React, { useCallback, useMemo, useEffect } from 'react'
import { App } from 'antd'
import { IntlProvider } from 'react-intl'
import AppLocale from '@vacationtracker/shared/i18n/language-provider'

import { useMultiStepForm, StepConfig, StepComponentProps } from '../../util/hooks/use-multi-step-form'
import { getOnboardingV2Progress } from '../../util/onboarding-v2-storage'
import { track } from '../../services/analytics/analytics'
import { store } from '../../store/configure'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'

import { HeatmapData } from '../../routes/DashboardPage/types'
import { WelcomeStep } from './steps/WelcomeStep'
import { LocationSetupStep } from './steps/LocationSetupStep'
import { DepartmentSetupStep } from './steps/DepartmentSetupStep'
import { LeaveRequestStep } from './steps/LeaveRequestStep'
import { CompletionStep } from './steps/CompletionStep'
import { OnboardingProvider, useOnboardingContext } from './context'
import { shouldApplyOnboardingCallWelcomeModalExperimentLayout, useOnboardingCallCtaVariant, type OnboardingCallCtaVariantId } from '../onboarding-call-cta'

import { IUserInfo } from '../../types/users'
import { ILocale } from '@vacationtracker/shared/types/i18n'
import { ICountry } from '@vacationtracker/shared/data/countries'
import { ILeavePolicy } from '../../types/locations'
import './styles.less'

// Re-export for step components
export type { StepComponentProps }

// Define the shape of our onboarding data
export interface OnboardingV2Data {
  // Welcome step data
  hasSeenWelcome?: boolean
  // Location step data
  locationName?: string
  locationTimezone?: string
  country?: ICountry
  leavePolicies?: ILeavePolicy[]
  // Department step data
  departmentName?: string
  // Leave request step data
  leaveType?: string
  leaveStartDate?: string
  leaveEndDate?: string
  leaveReason?: string
  // Completion step data
  hasCompletedOnboarding?: boolean
  // Consolidated data from props (optional for step components)
  authUser?: IUserInfo
  departments?: HeatmapData['departments']
  holidays?: HeatmapData['holidays']
}

interface OnboardingV2ModalProps {
  onClose?: () => void
  onExploreFirst?: () => void
  onMinimize?: () => void
  authUser?: IUserInfo
  locale?: ILocale
  heatmapData?: HeatmapData
}

/** Imperative `showOnboardingV2Modal` may pass variant so modal width matches PostHog before inner hooks resolve. */
export type ShowOnboardingV2ModalProps = Omit<OnboardingV2ModalProps, 'authUser' | 'locale' | 'heatmapData'> & {
  onboardingCallCtaVariant?: OnboardingCallCtaVariantId
}

// Define steps outside component to prevent recreation on each render
const ONBOARDING_STEPS: StepConfig<OnboardingV2Data>[] = [
  {
    id: 'welcome',
    component: WelcomeStep,
  },
  {
    id: 'location-setup',
    component: LocationSetupStep,
  },
  {
    id: 'department-setup',
    component: DepartmentSetupStep,
  },
  {
    id: 'leave-request',
    component: LeaveRequestStep,
  },
  {
    id: 'completion',
    component: CompletionStep,
  },
]

// Function to extract initial data from user info and heatmap data
const extractInitialData = (authUser?: IUserInfo, heatmapData?: HeatmapData): OnboardingV2Data => {
  return {
    hasSeenWelcome: false,
    locationName: authUser?.location?.name || '',
    locationTimezone: authUser?.location?.timezone || '',
    departmentName: authUser?.team?.name || '',
    hasCompletedOnboarding: false,
    authUser,
    departments: heatmapData?.departments || [],
    holidays: heatmapData?.holidays || [],
  }
}

const OnboardingV2ModalContent: React.FC<OnboardingV2ModalProps> = ({ onExploreFirst, onMinimize, authUser, locale, heatmapData }) => {
  const { currentStep, currentStepIndex, formData, isLastStep, isLoading, errors, updateFormData, goToNext, goToStep, submitForm } = useMultiStepForm<OnboardingV2Data>({
    steps: ONBOARDING_STEPS,
    initialData: extractInitialData(authUser, heatmapData),
    // Start at welcome; we'll immediately bump to next incomplete via effect below
    initialStepIndex: 0,
  })

  // Read user/company from provider context to determine resume step
  const { userId, companyId } = useOnboardingContext() as any

  // On mount (and when IDs available), navigate to first incomplete relevant step
  useEffect(() => {
    if (!userId || !companyId) return
    const progress = getOnboardingV2Progress(userId, companyId)
    const completed = progress?.completedSteps || []
    const progressStarted = !!progress && (completed.length > 0 || (progress.currentStepIndex ?? 0) > 0 || !!progress.hasCompletedOnboarding)
    if (!progressStarted) return

    const ordered = ['location-setup', 'department-setup', 'leave-request']
    const nextId = ordered.find((id) => !completed.includes(id)) || 'completion'
    const targetIndex = ONBOARDING_STEPS.findIndex((s) => s.id === nextId)
    if (targetIndex >= 0 && targetIndex !== currentStepIndex) {
      goToStep(targetIndex)
    }
  }, [userId, companyId])

  const handleClose = useCallback(() => {
    // Minimize instead of fully dismissing
    onMinimize?.()
  }, [onMinimize])

  const CurrentStepComponent = currentStep.component
  const onboardingCallCtaVariant = useOnboardingCallCtaVariant()
  const contextualCallCtaWelcomeLayout = shouldApplyOnboardingCallWelcomeModalExperimentLayout(authUser, onboardingCallCtaVariant)

  return (
    <div className={contextualCallCtaWelcomeLayout ? 'onboarding-v2-modal onboarding-v2-modal--call-cta-contextual-welcome' : 'onboarding-v2-modal'}>
      <div className="onboarding-content">
        <CurrentStepComponent
          data={formData}
          updateData={updateFormData}
          errors={errors}
          isLoading={isLoading}
          onNext={isLastStep ? submitForm : goToNext}
          onExploreFirst={onExploreFirst}
          onClose={handleClose}
          isLastStep={isLastStep}
          currentStepIndex={currentStepIndex}
          authUser={authUser}
          locale={locale}
          heatmapData={heatmapData}
        />
      </div>
    </div>
  )
}

// Memoize the component to prevent unnecessary re-renders
export const OnboardingV2Modal: React.FC<OnboardingV2ModalProps> = ({ onClose, onExploreFirst, onMinimize, authUser, locale, heatmapData }) => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const initialData = useMemo(() => extractInitialData(authUser, heatmapData), [authUser, heatmapData])

  return (
    <OnboardingProvider initialData={initialData} steps={ONBOARDING_STEPS} userId={authUser?.id} companyId={authCompany?.id}>
      <OnboardingV2ModalContent onClose={onClose} onExploreFirst={onExploreFirst} onMinimize={onMinimize} authUser={authUser} locale={locale} heatmapData={heatmapData} />
    </OnboardingProvider>
  )
}

// Helper function to show the modal imperatively
// This accepts the modal instance from App.useApp() to avoid hook usage outside components
export const showOnboardingV2Modal = (
  modal: ReturnType<typeof App.useApp>['modal'],
  authUser: IUserInfo,
  locale: ILocale,
  heatmapData: HeatmapData,
  props?: ShowOnboardingV2ModalProps
) => {
  const { onboardingCallCtaVariant, ...modalRestProps } = props ?? {}
  const widthVariant = onboardingCallCtaVariant ?? 'contextual_and_menu'
  let modalRef: any = null

  const handleClose = () => {
    if (modalRef) {
      modalRef.destroy()
      modalRef = null
    }
    modalRestProps.onClose?.()
  }

  const handleMinimize = () => {
    if (modalRef) {
      modalRef.destroy()
      modalRef = null
    }
    modalRestProps.onMinimize?.()
  }

  const handleExploreFirst = () => {
    if (modalRef) {
      modalRef.destroy()
      modalRef = null
    }
    modalRestProps.onExploreFirst?.()
  }

  // Get the messages for the current locale
  const localeMessages = AppLocale[locale.locale]?.messages || AppLocale['en-US'].messages

  modalRef = modal.info({
    title: null,
    icon: null,
    width: shouldApplyOnboardingCallWelcomeModalExperimentLayout(authUser, widthVariant) ? 1320 : 1200,
    closable: true,
    maskClosable: false,
    centered: true,
    className: 'onboarding-v2-modal-wrapper',
    content: (
      <IntlProvider locale={locale.locale} messages={localeMessages}>
        <OnboardingV2Modal
          {...modalRestProps}
          authUser={authUser}
          locale={locale}
          heatmapData={heatmapData}
          onClose={handleClose}
          onMinimize={handleMinimize}
          onExploreFirst={handleExploreFirst}
        />
      </IntlProvider>
    ),
    footer: null,
    onCancel: () => {
      // Track X button dismissal for welcome screen
      const companyId = store.getState()?.authCompanySlice?.authCompany?.id
      if (authUser && companyId) {
        track('ONBOARDING_WELCOME_SCREEN_DISMISSED', {
          dismiss_method: 'X_BUTTON',
          company_id: companyId,
          user_id: authUser.id,
        })
      }
      handleMinimize()
    },
  })

  return modalRef
}
